import React, { useState, useEffect } from 'react';
import { RefreshCw, Wifi, WifiOff, Mail, ArrowUpRight, ArrowDownLeft, BarChart3, Users } from 'lucide-react';
import { API_BASE, apiFetch } from '../config';
import { useLanguage } from '../i18n';

const Dashboard = ({ stats, onCheckMail }) => {
  const { t } = useLanguage();
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [topSenders, setTopSenders] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [dailyRes, sendersRes] = await Promise.all([
        apiFetch(API_BASE + '/api/analytics/daily-emails'),
        apiFetch(API_BASE + '/api/analytics/top-senders'),
      ]);
      if (dailyRes.ok) {
        const data = await dailyRes.json();
        setDailyData(data.data || []);
      }
      if (sendersRes.ok) {
        const data = await sendersRes.json();
        setTopSenders(data.data || []);
      }
    } catch (err) {
      console.error('Analytics xatosi:', err);
    }
  };

  const handleCheck = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const res = await apiFetch(`${API_BASE}/api/check-mail`, { method: 'POST' });
      const data = await res.json();
      setCheckResult(data);
      if (onCheckMail) onCheckMail();
    } catch {
      setCheckResult({ success: false, message: t('dashboard_server_error') });
    }
    setTimeout(() => setChecking(false), 1000);
    setTimeout(() => setCheckResult(null), 4000);
  };

  const isOnline = stats.status === 'Online';

  return (
    <div className="screen-enter space-y-5 pb-24">
      <div className="glass-card p-6 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-cyan-500/15 rounded-full blur-2xl" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Mail size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold gradient-text">{t('dashboard_title')}</h1>
              <p className="text-xs text-white/40 font-medium">{t('dashboard_subtitle')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card p-4 animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isOnline ? 'bg-emerald-500/10' : 'bg-red-500/10'
            }`}>
              {isOnline ? (
                <Wifi size={18} className="text-emerald-400" />
              ) : (
                <WifiOff size={18} className="text-red-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-white/90">{t('dashboard_server')}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <div className={`${isOnline ? 'status-online' : 'status-offline'} animate-pulse-glow`} />
                <span className={`text-xs font-medium ${isOnline ? 'text-emerald-400' : 'text-red-400'}`}>
                  {isOnline ? t('dashboard_connected') : t('dashboard_disconnected')}
                </span>
              </div>
            </div>
          </div>
          {stats.last_check && (
            <span className="text-[10px] text-white/30 text-right">
              {t('dashboard_last_check')}<br />
              {new Date(stats.last_check).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      <button
        id="check-mail-btn"
        onClick={handleCheck}
        disabled={checking}
        className="w-full btn-primary flex items-center justify-center gap-3 animate-fade-in-up stagger-2 disabled:opacity-50"
        style={{ opacity: 0 }}
      >
        <RefreshCw size={18} className={checking ? 'animate-spin' : ''} />
        <span>{checking ? t('dashboard_checking') : t('dashboard_check_mail')}</span>
      </button>

      {checkResult && (
        <div className={`glass-card p-3 text-center text-sm font-medium animate-fade-in-up ${
          checkResult.success ? 'text-emerald-400 border-emerald-500/20' : 'text-red-400 border-red-500/20'
        }`}>
          {checkResult.message}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="glass-card p-4 animate-fade-in-up stagger-3 group" style={{ opacity: 0 }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
              <ArrowDownLeft size={16} className="text-blue-400" />
            </div>
            <span className="text-xs text-white/40 font-medium uppercase tracking-wider">{t('dashboard_received')}</span>
          </div>
          <p className="text-3xl font-bold text-white/90 font-mono">{stats.received || 0}</p>
          <p className="text-[10px] text-white/30 mt-1">{t('dashboard_received_desc')}</p>
        </div>

        <div className="glass-card p-4 animate-fade-in-up stagger-4 group" style={{ opacity: 0 }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
              <ArrowUpRight size={16} className="text-cyan-400" />
            </div>
            <span className="text-xs text-white/40 font-medium uppercase tracking-wider">{t('dashboard_sent')}</span>
          </div>
          <p className="text-3xl font-bold text-white/90 font-mono">{stats.sent || 0}</p>
          <p className="text-[10px] text-white/30 mt-1">{t('dashboard_sent_desc')}</p>
        </div>
      </div>

      <div className="glass-card p-4 animate-fade-in-up stagger-5" style={{ opacity: 0 }}>
        <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">{t('dashboard_info_title')}</h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className="text-white/60">{t('dashboard_info_server')}</span>
            <span className="text-white/90 font-mono text-xs">{stats.server || 'Gmail API'}</span>
          </div>
          <div className="h-px bg-white/5" />
          <div className="flex justify-between items-center text-sm">
            <span className="text-white/60">{t('dashboard_info_user')}</span>
            <span className="text-white/90 font-mono text-xs">{stats.user || '—'}</span>
          </div>
          <div className="h-px bg-white/5" />
          <div className="flex justify-between items-center text-sm">
            <span className="text-white/60">{t('dashboard_info_interval')}</span>
            <span className="text-white/90 font-mono text-xs">{stats.interval || 60} {t('dashboard_info_seconds')}</span>
          </div>
        </div>
      </div>

      {/* 14 kunlik email grafigi */}
      {dailyData.length > 0 && (
        <div className="glass-card p-4 animate-fade-in-up stagger-6" style={{ opacity: 0 }}>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={14} className="text-blue-400" />
            <span className="text-xs text-white/50 uppercase font-semibold">14 kunlik xatlar</span>
          </div>
          {(() => {
            const maxVal = Math.max(...dailyData.map(d => d.count), 1);
            return (
              <div className="flex items-end justify-between gap-1 h-[80px]">
                {dailyData.map((day, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                    {day.count > 0 && (
                      <span className="text-[8px] text-white/40 font-mono">{day.count}</span>
                    )}
                    <div className="w-full relative flex-1 flex items-end">
                      <div
                        className="w-full rounded-t bg-gradient-to-t from-blue-500 to-cyan-400 transition-all duration-700"
                        style={{ height: Math.max((day.count / maxVal) * 100, 3) + '%', minHeight: '2px' }}
                      />
                    </div>
                    <span className="text-[7px] text-white/30">{day.day}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Top senders */}
      {topSenders.length > 0 && (
        <div className="glass-card p-4 animate-fade-in-up" style={{ opacity: 0, animationDelay: '0.35s' }}>
          <div className="flex items-center gap-2 mb-3">
            <Users size={14} className="text-cyan-400" />
            <span className="text-xs text-white/50 uppercase font-semibold">Eng faol manzillar</span>
          </div>
          <div className="space-y-2">
            {topSenders.slice(0, 5).map((sender, i) => {
              const maxCount = topSenders[0]?.count || 1;
              const percent = (sender.count / maxCount) * 100;
              return (
                <div key={i}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] text-white/60 font-mono truncate max-w-[70%]">{sender.short}</span>
                    <span className="text-[10px] text-white/80 font-bold">{sender.count}</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: percent + '%',
                        background: i === 0 ? 'linear-gradient(90deg, #3b82f6, #06b6d4)' :
                                   i === 1 ? 'linear-gradient(90deg, #06b6d4, #10b981)' :
                                   'rgba(148, 163, 184, 0.3)'
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
